import { useContext, useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/system';
import { useParams } from 'react-router-dom';
import { captableAction } from 'common/actions';
import {
  BREAKPOINT_ANALYSIS_SLUG,
  BREAKPOINT_ANALYSIS_TITLE,
  CAP_TABLE_SLUG,
  CAP_TABLE_TITLE,
  FUND_OWNERSHIP_SLUG,
  FUND_OWNERSHIP_TITLE,
} from 'common/constants/cap-table';
import { useCompanyBreadcrumb } from 'common/hooks';
import { useStore } from 'common/store';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport';
import { CustomContainer } from 'components';
import ExcelExportContext from 'context/ExcelExportContext';
import LayoutContext from 'context/LayoutContext';
import { CapTableDialog, NewCompanyDialog } from 'layouts/Main/components';
import useCapTableVersionFilter from 'pages/CapTable/cap-table/utilities/useCapTableVersionFilter';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { useCompanyMeasurementDateFilter } from 'services/hooks';
import { useGetAllocationStatusBreadcrumb } from 'services/hooks/allocation';
import { getObjectValue } from 'utilities';
import BreakpointAnalysis from './breakpoint-analysis';
import CapTable from './cap-table';
import FundOwnership from './fund-ownership';

const CAP_TABLE_CONTAINER_CLASS = `${CAP_TABLE_SLUG}-container`;
const FUND_OWNERSHIP_CONTAINER_CLASS = `${FUND_OWNERSHIP_SLUG}-container`;
const BREAKPOINT_ANALYSIS_CONTAINER_CLASS = `${BREAKPOINT_ANALYSIS_SLUG}-container`;

const CapTableView = () => {
  const { tableSlugParam } = useParams();
  const [{ captableInfo }, dispatch] = useStore();
  const { id: currentCapTableId } = getObjectValue(captableInfo);
  const { setCurrentSelectedValues } = useContext(ExcelExportContext);

  const { showPageForm, togglePageForm, isExportMode } = useContext(LayoutContext);
  const [showRelatedEntityDialog, setShowRelatedEntityDialog] = useState(false);
  const toggleRelatedEntityDialog = () => setShowRelatedEntityDialog(!showRelatedEntityDialog);
  // Filters options
  const { MDDialogue, selectedMeasurementDate } = useCompanyMeasurementDateFilter();
  const {
    selectedVersion,
    onEditCaptable,
    updateCapTableVersions,
    saveAsNewProforma,
    versionName,
    onAddRelatedEntity,
    capTableVersionsByCompany,
  } = useCapTableVersionFilter({ 
    selectedMeasurementDate, 
    onAddRelatedEntity: toggleRelatedEntityDialog,
    togglePageForm,
  });

  const { allocationStatusBreadcrumb, reloadAllocationBreadcrumbInfo } = useGetAllocationStatusBreadcrumb();

  useEffect(() => {
    setCurrentSelectedValues({
      cap_table: currentCapTableId,
      fo_cap_table: currentCapTableId,
      breakpoint_cap_table: currentCapTableId,
      waterfall_cap_table: currentCapTableId,
    });
  }, [currentCapTableId, setCurrentSelectedValues]);

  const activeTable = useMemo(() => {
    switch (tableSlugParam) {
      case CAP_TABLE_SLUG:
        return CAP_TABLE_TITLE;
      case FUND_OWNERSHIP_SLUG:
        return FUND_OWNERSHIP_TITLE;
      case BREAKPOINT_ANALYSIS_SLUG:
        return BREAKPOINT_ANALYSIS_TITLE;
      default:
        return '';
    }
  }, [tableSlugParam]);

  const { icon: status, title: label } = allocationStatusBreadcrumb;

  const captableBreadcrumb = useMemo(
    () => ({
      page: activeTable,
      item: versionName
        ? {
          label: versionName,
          allocationVersion: {
            label,
            status,
          },
        }
        : {},
    }),
    [activeTable, label, status, versionName]
  );

  useCompanyBreadcrumb(captableBreadcrumb);

  // Clean cap table info in the global store when leaving the page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => dispatch(captableAction.setCaptableInfo({})), []);

  return (
    <>
      {(selectedVersion || !selectedMeasurementDate) && tableSlugParam === CAP_TABLE_SLUG && (
        <CustomContainer className={CAP_TABLE_CONTAINER_CLASS}>
          <Grid size={{ xs: 12 }}>
            <CapTable
              onEditCaptable={onEditCaptable}
              updateCapTableVersions={updateCapTableVersions}
              saveAsNewProforma={saveAsNewProforma}
              selectedMeasurementDate={selectedMeasurementDate}
              capTableId={currentCapTableId}
            />
          </Grid>
        </CustomContainer>
      )}

      {(selectedVersion || !selectedMeasurementDate) && tableSlugParam === FUND_OWNERSHIP_SLUG && (
        <CustomContainer className={FUND_OWNERSHIP_CONTAINER_CLASS} data-ref={PdfExportTargets.FUND_OWNERSHIP.id} style={isExportMode ? EXPORT_MODE_STYLES : {}}>
          <Grid size={{ xs: 12 }} style={isExportMode ? EXPORT_MODE_STYLES : {}}>
            <FundOwnership
              updateCapTableVersions={updateCapTableVersions}
              saveAsNewProforma={saveAsNewProforma}
              selectedMeasurementDate={selectedMeasurementDate}
              reloadAllocationBreadcrumbInfo={reloadAllocationBreadcrumbInfo}
              capTableVersionsByCompany={capTableVersionsByCompany}
            />
          </Grid>
        </CustomContainer>
      )}

      {tableSlugParam === BREAKPOINT_ANALYSIS_SLUG && (
        <CustomContainer className={BREAKPOINT_ANALYSIS_CONTAINER_CLASS}>
          <Grid size={{ xs: 12 }}>
            <BreakpointAnalysis
              updateCapTableVersions={updateCapTableVersions}
              saveAsNewProforma={saveAsNewProforma}
              selectedMeasurementDate={selectedMeasurementDate}
            />
          </Grid>
        </CustomContainer>
      )}

      <NewCompanyDialog
        open={showRelatedEntityDialog}
        onClose={toggleRelatedEntityDialog}
        onSave={onAddRelatedEntity}
        isRelatedEntity
      />

      {captableInfo && <CapTableDialog open={showPageForm} onClose={togglePageForm} onSave={onEditCaptable} />}
      <MDDialogue />
    </>
  );
};

export default CapTableView;

export { CapTableView, CapTable, FundOwnership, BreakpointAnalysis };
